export const escapeHtml = (value = '') =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const plainTextToHtml = (value = '') => {
  const safe = escapeHtml(value);
  return safe
    .split(/\n{2,}/)
    .map((block) => `<p>${block.replace(/\n/g, '<br />')}</p>`)
    .join('');
};

export const normalizeRichText = (value = '') => {
  if (!value) return '';
  if (/<[a-z][\s\S]*>/i.test(value)) {
    return value;
  }
  return plainTextToHtml(value);
};

const unwrapNode = (node) => {
  const parent = node.parentNode;
  if (!parent) return;
  while (node.firstChild) {
    parent.insertBefore(node.firstChild, node);
  }
  parent.removeChild(node);
};

const replaceNode = (node, tagName) => {
  const parent = node.parentNode;
  if (!parent) return;
  const replacement = node.ownerDocument.createElement(tagName);
  while (node.firstChild) {
    replacement.appendChild(node.firstChild);
  }
  parent.replaceChild(replacement, node);
  return replacement;
};

export const sanitizeRichText = (value = '') => {
  if (!value) return '';
  const html = normalizeRichText(value);
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const container = doc.body;
  const allowedTags = new Set([
    'b',
    'strong',
    'i',
    'em',
    'u',
    'p',
    'div',
    'br',
    'ul',
    'ol',
    'li',
    'a',
    'code',
    'pre'
  ]);

  const sanitizeNode = (node) => {
    const children = Array.from(node.childNodes);
    children.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) {
        node.removeChild(child);
        return;
      }

      const tag = child.tagName.toLowerCase();

      if (tag === 'span') {
        const style = (child.getAttribute('style') || '').toLowerCase();
        let replacement = child;
        if (style.includes('text-decoration') && style.includes('underline')) {
          replacement = replaceNode(child, 'u') || child;
        } else if (style.includes('font-weight') && style.includes('bold')) {
          replacement = replaceNode(child, 'strong') || child;
        } else if (style.includes('font-style') && style.includes('italic')) {
          replacement = replaceNode(child, 'em') || child;
        } else {
          unwrapNode(child);
          return;
        }
        sanitizeNode(replacement);
        return;
      }

      if (!allowedTags.has(tag)) {
        unwrapNode(child);
        return;
      }

      const attributes = Array.from(child.attributes);
      attributes.forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (tag === 'a' && (name === 'href' || name === 'target' || name === 'rel')) {
          return;
        }
        child.removeAttribute(attr.name);
      });

      if (tag === 'a') {
        const href = child.getAttribute('href') || '';
        const safeHref = href.trim();
        if (!safeHref || !/^(https?:|mailto:)/i.test(safeHref)) {
          unwrapNode(child);
          return;
        }
        child.setAttribute('href', safeHref);
        child.setAttribute('target', '_blank');
        child.setAttribute('rel', 'noopener noreferrer');
      }

      sanitizeNode(child);
    });
  };

  sanitizeNode(container);
  return container.innerHTML.trim();
};

export const richTextToPlainText = (value = '') => {
  if (!value) return '';
  const html = sanitizeRichText(value);
  const withBreaks = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n');
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${withBreaks}</div>`, 'text/html');
  const text = doc.body.textContent || '';
  return text.replace(/\n{3,}/g, '\n\n').trim();
};
